import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Container, Card, CardContent, Button, TextField, MenuItem,
  FormControl, InputLabel, Select, Slider, Alert, CircularProgress, Chip,
  Tooltip, IconButton,
} from '@mui/material';
import { Info, Shield } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { paperTradingApi, BudgetOverview } from '../../services/paperTradingApi';
import { MARKETS } from '../../constants/markets';

import { apiFetch } from '../../utils/apiFetch';

const STRATEGY_LABELS: Record<string, string> = {
  marktmeister: 'MarktMeister',
};

const INTERVAL_LABELS: Record<number, string> = {
  60: '1 Minute', 300: '5 Minuten', 900: '15 Minuten', 3600: '1 Stunde', 86400: '1 Tag',
};

const ALLOWED_CHART_INTERVALS: Record<string, { value: string; label: string }[]> = {
  marktmeister:          [{ value: '5m', label: '5 Minuten' }, { value: '15m', label: '15 Minuten' }, { value: '1h', label: '1 Stunde' }],
};

const DEFAULT_CHART_INTERVAL: Record<string, string> = {
  marktmeister: '5m',
};

interface StrategyData {
  id: number;
  name: string;
  strategy_type: string;
  market: string;
  budget_eur: number;
  budget_used_eur: number;
  is_active: boolean;
  interval_seconds: number;
  total_trades: number;
  stop_loss_percentage?: number | null;
  take_profit_percentage?: number | null;
  min_trade_amount_eur?: number | null;
  min_trade_behavior?: 'skip' | 'round_up' | null;
  last_signal: { action: string; reason: string } | null;
  last_signal_at: string | null;
  last_executed: string | null;
}

const StrategyEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [strategy, setStrategy] = useState<StrategyData | null>(null);
  const [budgetData, setBudgetData] = useState<BudgetOverview | null>(null);

  // Editable fields
  const [name, setName] = useState('');
  const [market, setMarket] = useState('BTC-EUR');
  const [intervalSeconds, setIntervalSeconds] = useState(300);
  const [budgetEur, setBudgetEur] = useState(1000);
  const [stopLoss, setStopLoss] = useState<number>(5);
  const [takeProfit, setTakeProfit] = useState<number>(10);
  const [chartInterval, setChartInterval] = useState<string>('');
  const [minTradeAmount, setMinTradeAmount] = useState<number>(0);
  const [minTradeBehavior, setMinTradeBehavior] = useState<'skip' | 'round_up'>('skip');

  useEffect(() => {
    const loadData = async () => {
      try {
        const budget = await paperTradingApi.getBudget();
        setBudgetData(budget);

        const found = budget.strategies.find((s) => s.id === Number(id));
        if (!found) {
          setError('Strategie nicht gefunden');
          setLoading(false);
          return;
        }

        setStrategy(found);
        setName(found.name);
        setMarket(found.market || 'BTC-EUR');
        setIntervalSeconds(found.interval_seconds || 300);
        setBudgetEur(found.budget_eur || 0);
        setStopLoss(found.stop_loss_percentage ?? 5);
        setTakeProfit(found.take_profit_percentage ?? 10);
        setMinTradeAmount(Number(found.min_trade_amount_eur ?? 0));
        setMinTradeBehavior((found.min_trade_behavior as 'skip' | 'round_up') || 'skip');
        setChartInterval((found as any).config?.interval || DEFAULT_CHART_INTERVAL[found.strategy_type] || '1h');
      } catch (e: any) {
        setError(e.message || 'Fehler beim Laden');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [id]);

  // Available budget = total available + current strategy's budget (since we're re-allocating)
  const availableBudget = (budgetData?.available_eur ?? 0) + (strategy?.budget_eur ?? 0);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      // Update strategy fields
      const res = await apiFetch(`/strategies/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          interval_seconds: intervalSeconds,
          budget_eur: budgetEur,
          config: { market, interval: chartInterval },
          stop_loss_percentage: stopLoss,
          take_profit_percentage: takeProfit,
          min_trade_amount_eur: minTradeAmount > 0 ? minTradeAmount : null,
          min_trade_behavior: minTradeAmount > 0 ? minTradeBehavior : null,
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Fehler beim Speichern');
      }

      // Update budget via paper trading endpoint
      await apiFetch(`/paper/strategies/${id}/budget`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ budget_eur: budgetEur }),
      });

      setSuccess(true);
      setTimeout(() => navigate('/strategies'), 1200);
    } catch (e: any) {
      setError(e.message || 'Fehler beim Speichern der Strategie');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!strategy) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{error || 'Strategie nicht gefunden'}</Alert>
        <Button onClick={() => navigate('/strategies')} sx={{ mt: 2 }}>Zurück</Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 2 }}>
      <Typography variant="h5" fontWeight={700} gutterBottom>Strategie bearbeiten</Typography>

      <Card sx={{ borderRadius: 3, mb: 3 }}>
        <CardContent>
          {/* Strategy type (read-only) */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <Chip
              label={STRATEGY_LABELS[strategy.strategy_type] || strategy.strategy_type}
              color="primary"
            />
            {strategy.is_active && <Chip label="Aktiv" color="success" size="small" variant="outlined" />}
            <Typography variant="body2" color="text.secondary">
              {strategy.total_trades} Trades ausgeführt
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField
              fullWidth
              label="Name der Strategie"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />

            <FormControl fullWidth>
              <InputLabel>Markt</InputLabel>
              <Select value={market} label="Markt" onChange={(e) => setMarket(e.target.value)}>
                {MARKETS.map((m) => (
                  <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {strategy && ALLOWED_CHART_INTERVALS[strategy.strategy_type] && (
              <FormControl fullWidth>
                <InputLabel>Chart-Zeitraum (Kerzengröße)</InputLabel>
                <Select value={chartInterval} label="Chart-Zeitraum (Kerzengröße)" onChange={(e) => setChartInterval(e.target.value)}>
                  {ALLOWED_CHART_INTERVALS[strategy.strategy_type].map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <FormControl fullWidth>
              <InputLabel>Prüf-Intervall</InputLabel>
              <Select
                value={intervalSeconds}
                label="Prüf-Intervall"
                onChange={(e) => setIntervalSeconds(Number(e.target.value))}
              >
                {Object.entries(INTERVAL_LABELS).map(([val, label]) => (
                  <MenuItem key={val} value={Number(val)}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      {/* Risikomanagement */}
      <Card sx={{ borderRadius: 3, mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Shield color="warning" />
            <Typography variant="h6">Risikomanagement</Typography>
          </Box>

          {/* Stop-Loss */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="subtitle2" fontWeight={600} color="error.main">
                Stop-Loss: {stopLoss}%
              </Typography>
              <Tooltip title="Automatischer Verkauf wenn der Kurs um diesen Prozentsatz fällt. Engerer Stop = weniger Risiko aber öfter ausgelöst. Weiter Stop = mehr Spielraum aber mehr Risiko pro Trade." arrow>
                <IconButton size="small"><Info sx={{ fontSize: 16 }} /></IconButton>
              </Tooltip>
            </Box>
            <Slider
              value={stopLoss}
              onChange={(_, v) => setStopLoss(v as number)}
              min={0.5} max={15} step={0.5}
              valueLabelDisplay="auto"
              valueLabelFormat={(v) => `${v}%`}
              sx={{ color: 'error.main' }}
              marks={[{ value: 1, label: '1%' }, { value: 5, label: '5%' }, { value: 10, label: '10%' }, { value: 15, label: '15%' }]}
            />
          </Box>

          {/* Take-Profit */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="subtitle2" fontWeight={600} color="success.main">
                Take-Profit: {takeProfit}%
              </Typography>
              <Tooltip title="Automatischer Verkauf wenn der Kurs um diesen Prozentsatz steigt. Niedrigeres Ziel wird häufiger erreicht, höheres Ziel bringt mehr pro Trade." arrow>
                <IconButton size="small"><Info sx={{ fontSize: 16 }} /></IconButton>
              </Tooltip>
            </Box>
            <Slider
              value={takeProfit}
              onChange={(_, v) => setTakeProfit(v as number)}
              min={1} max={30} step={0.5}
              valueLabelDisplay="auto"
              valueLabelFormat={(v) => `${v}%`}
              sx={{ color: 'success.main' }}
              marks={[{ value: 2, label: '2%' }, { value: 5, label: '5%' }, { value: 10, label: '10%' }, { value: 20, label: '20%' }, { value: 30, label: '30%' }]}
            />
          </Box>

          {/* Risk/Reward */}
          <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Risk/Reward</Typography>
              <Typography variant="h6" fontWeight={700} color={takeProfit / stopLoss >= 1.5 ? 'success.main' : 'warning.main'}>
                1 : {(takeProfit / stopLoss).toFixed(1)}
              </Typography>
            </Box>
            <Tooltip title="Ein R:R von mindestens 1:1.5 gilt als gut. Ab 1:2 ist es sehr profitabel." arrow>
              <Chip
                label={takeProfit / stopLoss >= 2 ? 'Sehr gut' : takeProfit / stopLoss >= 1.5 ? 'Gut' : takeProfit / stopLoss >= 1 ? 'Akzeptabel' : 'Riskant'}
                color={takeProfit / stopLoss >= 1.5 ? 'success' : takeProfit / stopLoss >= 1 ? 'warning' : 'error'}
                size="small"
                sx={{ cursor: 'help' }}
              />
            </Tooltip>
          </Box>

          {/* Mindest-Trade-Betrag */}
          <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                Mindest-Trade-Betrag
              </Typography>
              <Tooltip
                title={
                  <Box>
                    <Typography variant="caption" component="div" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Wofür ist das gut?
                    </Typography>
                    <Typography variant="caption" component="div">
                      Bitvavo nimmt 0,25% Gebühr pro Trade. Bei kleinen Beträgen frisst das die Rendite.
                      Mit dem Mindest-Trade-Betrag stellst du sicher, dass nur lohnende Orders ausgeführt werden.
                      Das absolute Minimum bei Bitvavo ist 5&nbsp;EUR.
                    </Typography>
                  </Box>
                }
                arrow
              >
                <IconButton size="small"><Info sx={{ fontSize: 16 }} /></IconButton>
              </Tooltip>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <TextField
                type="number"
                label="Mindestbetrag (EUR)"
                value={minTradeAmount || ''}
                onChange={(e) => setMinTradeAmount(Math.max(0, Number(e.target.value) || 0))}
                inputProps={{ min: 0, step: 5 }}
                helperText={minTradeAmount > 0 && minTradeAmount < 5 ? 'Mindestens 5 EUR (Bitvavo-Limit)' : '0 = kein Limit'}
                error={minTradeAmount > 0 && minTradeAmount < 5}
                size="small"
                sx={{ flex: '1 1 200px' }}
              />
              <FormControl sx={{ flex: '1 1 240px' }} disabled={minTradeAmount === 0} size="small">
                <InputLabel>Verhalten</InputLabel>
                <Select
                  value={minTradeBehavior}
                  label="Verhalten"
                  onChange={(e) => setMinTradeBehavior(e.target.value as 'skip' | 'round_up')}
                >
                  <MenuItem value="skip">Trade überspringen</MenuItem>
                  <MenuItem value="round_up">Auf Mindestbetrag aufrunden</MenuItem>
                </Select>
              </FormControl>
            </Box>
            {minTradeAmount > 0 && (
              <Alert severity="info" sx={{ mt: 1.5 }}>
                {minTradeBehavior === 'skip'
                  ? `Wenn ein Signal weniger als ${minTradeAmount} EUR handeln will, wird der Trade übersprungen.`
                  : `Wenn ein Signal weniger als ${minTradeAmount} EUR handeln will, wird die Order auf ${minTradeAmount} EUR aufgestockt.`}
              </Alert>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Budget */}
      <Card sx={{ borderRadius: 3, mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Budget</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Verfügbar: €{availableBudget.toLocaleString('de-DE', { maximumFractionDigits: 0 })}
            {strategy.budget_used_eur > 0 && (
              <> · Bereits verwendet: €{strategy.budget_used_eur.toFixed(0)}</>
            )}
          </Typography>

          <Typography variant="h4" textAlign="center" fontWeight={700} color="primary" sx={{ mb: 2 }}>
            €{budgetEur.toLocaleString('de-DE')}
          </Typography>
          <Slider
            value={budgetEur}
            onChange={(_, v) => setBudgetEur(v as number)}
            min={Math.max(Math.ceil(strategy.budget_used_eur || 0), 100)}
            max={Math.max(availableBudget, 100)}
            step={100}
            valueLabelDisplay="auto"
            valueLabelFormat={(v) => `€${v.toLocaleString('de-DE')}`}
          />
        </CardContent>
      </Card>

      {/* Summary */}
      <Card sx={{ borderRadius: 3, mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>Zusammenfassung der Änderungen</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {name !== strategy.name && (
              <Typography variant="body2">
                Name: <s>{strategy.name}</s> → <b>{name}</b>
              </Typography>
            )}
            {market !== (strategy.market || 'BTC-EUR') && (
              <Typography variant="body2">
                Markt: <s>{strategy.market}</s> → <b>{market}</b>
              </Typography>
            )}
            {intervalSeconds !== (strategy.interval_seconds || 300) && (
              <Typography variant="body2">
                Intervall: <s>{INTERVAL_LABELS[strategy.interval_seconds] || `${strategy.interval_seconds}s`}</s> → <b>{INTERVAL_LABELS[intervalSeconds] || `${intervalSeconds}s`}</b>
              </Typography>
            )}
            {budgetEur !== (strategy.budget_eur || 0) && (
              <Typography variant="body2">
                Budget: <s>€{(strategy.budget_eur || 0).toLocaleString('de-DE')}</s> → <b>€{budgetEur.toLocaleString('de-DE')}</b>
              </Typography>
            )}
            {name === strategy.name &&
              market === (strategy.market || 'BTC-EUR') &&
              intervalSeconds === (strategy.interval_seconds || 300) &&
              budgetEur === (strategy.budget_eur || 0) && (
                <Typography variant="body2" color="text.secondary">Keine Änderungen</Typography>
              )}
          </Box>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>Strategie erfolgreich gespeichert!</Alert>}

      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button onClick={() => navigate('/strategies')}>Abbrechen</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || budgetEur > availableBudget}
        >
          {saving ? <CircularProgress size={24} /> : 'Änderungen speichern'}
        </Button>
      </Box>
    </Container>
  );
};

export default StrategyEditPage;
